<template>
  <div class="min-h-screen bg-gray-50 flex flex-col font-sans">
    <!-- Header -->
    <header class="bg-white shadow-sm sticky top-0 z-30">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center">
            <img class="h-8 w-auto mr-3" src="@/assets/logo.png" alt="Logo" />
            <span class="text-xl font-bold text-gray-800 tracking-wide">中医智能问诊 · 用户端</span>
          </div>
          <div class="flex items-center space-x-4">
            <div v-if="patientInfo" class="flex items-center text-sm text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
              <span class="font-medium text-gray-900 mr-2">{{ patientInfo.name }}</span>
              <span>{{ patientInfo.gender === 1 ? '男' : '女' }} · {{ calculateAge(patientInfo.birthday) }}岁</span>
            </div>
            <button 
              v-if="patientInfo"
              @click="goToHistory"
              class="text-sm text-gray-600 hover:text-gray-800 font-medium"
            >
              问诊历史
            </button>
            <button 
              v-if="patientInfo"
              @click="showRegisterModal = true"
              class="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              修改信息
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1 max-w-7xl mx-auto w-full p-4 sm:p-6 lg:p-8">
      <!-- Tabs -->
      <div class="flex justify-center mb-8">
        <div class="bg-white p-1 rounded-xl shadow-sm border border-gray-100 inline-flex">
          <button 
            @click="currentTab = 'specialized'"
            class="px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none"
            :class="currentTab === 'specialized' ? 'bg-emerald-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-800'"
          >
            专科问诊
          </button>
          <button 
            @click="currentTab = 'open'"
            class="px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none"
            :class="currentTab === 'open' ? 'bg-emerald-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-800'"
          >
            开放问诊
          </button>
        </div>
      </div>

      <!-- Tab Content -->
      <div v-if="patientInfo && patientInfo.id">
        <transition name="fade" mode="out-in">
          <div v-if="currentTab === 'specialized'" key="specialized">
            <SpecializedDiagnosisPanel :patient-id="patientInfo.id" />
          </div>
          <div v-else key="open">
            <OpenDiagnosisPanel :patient-id="patientInfo.id" />
          </div>
        </transition>
      </div>
      
      <div v-else class="text-center py-20">
         <p class="text-gray-500">请先完善个人信息以开始问诊...</p>
      </div>
    </main>

    <!-- Register Modal -->
    <div v-if="showRegisterModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm px-4">
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 transform transition-all scale-100 animate-fade-in-up">
        <div class="text-center mb-8">
          <h2 class="text-2xl font-bold text-gray-800">完善用户信息</h2>
          <p class="text-sm text-gray-500 mt-2">请填写真实信息以便为您建立健康档案</p>
        </div>

        <form @submit.prevent="handleRegister" class="space-y-5">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">姓名</label>
            <input 
              v-model="form.name" 
              type="text" 
              required 
              placeholder="请输入您的真实姓名"
              class="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
            >
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">手机号</label>
            <input 
              v-model="form.mobile" 
              type="tel" 
              required 
              placeholder="请输入手机号码"
              class="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
            >
          </div>

          <div class="grid grid-cols-2 gap-5">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">性别</label>
              <div class="flex space-x-4 mt-1">
                <label class="flex items-center cursor-pointer">
                  <input type="radio" v-model="form.gender" :value="1" class="form-radio text-emerald-600 focus:ring-emerald-500 h-5 w-5">
                  <span class="ml-2 text-gray-700">男</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input type="radio" v-model="form.gender" :value="2" class="form-radio text-emerald-600 focus:ring-emerald-500 h-5 w-5">
                  <span class="ml-2 text-gray-700">女</span>
                </label>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">年龄</label>
              <input 
                v-model="form.age" 
                type="number" 
                min="0"
                placeholder="请输入年龄"
                required 
                class="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
              >
            </div>
          </div>

          <div v-if="errorMessage" class="text-red-500 text-sm bg-red-50 p-3 rounded-lg flex items-center">
            <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 001 1h.01a1 1 0 001-1V6a1 1 0 00-1-1H10z" clip-rule="evenodd"></path></svg>
            {{ errorMessage }}
          </div>

          <button 
            type="submit" 
            :disabled="loading"
            class="w-full bg-emerald-600 text-white py-3.5 rounded-xl font-bold hover:bg-emerald-700 transition shadow-lg shadow-emerald-100 disabled:opacity-70 disabled:cursor-not-allowed mt-4"
          >
            <span v-if="loading" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              提交中...
            </span>
            <span v-else>开始问诊</span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { httpRequest } from '@/utils/request'
import SpecializedDiagnosisPanel from './components/SpecializedDiagnosisPanel.vue'
import OpenDiagnosisPanel from './components/OpenDiagnosisPanel.vue'

export default {
  name: 'PatientHome',
  components: {
    SpecializedDiagnosisPanel,
    OpenDiagnosisPanel
  },
  setup() {
    const router = useRouter()
    const currentTab = ref('specialized')
    const showRegisterModal = ref(false)
    const loading = ref(false)
    const errorMessage = ref('')
    const patientInfo = ref(null)

    const form = reactive({
      name: '',
      mobile: '',
      gender: 1,
      age: ''
    })

    onMounted(() => {
      // Check if patient info exists in localStorage
      const storedInfo = localStorage.getItem('patient_info')
      if (storedInfo) {
        try {
          patientInfo.value = JSON.parse(storedInfo)
        } catch (e) {
          console.error('Failed to parse patient info', e)
          showRegisterModal.value = true
        }
      } else {
        showRegisterModal.value = true
      }
    })

    const calculateAge = (birthday) => {
      if (!birthday) return 0
      const birthDate = new Date(birthday)
      const difference = Date.now() - birthDate.getTime()
      const ageDate = new Date(difference)
      return Math.abs(ageDate.getUTCFullYear() - 1970)
    }

    const handleRegister = async () => {
      loading.value = true
      errorMessage.value = ''

      try {
        const birthYear = new Date().getFullYear() - (parseInt(form.age) || 0)
        const computedBirthday = `${birthYear}-01-01`

        const response = await httpRequest('post', '/v1/patient/register', {
          headers: {
            'Client-Type': '1'
          },
          body: {
            name: form.name,
            mobile: form.mobile,
            gender: form.gender,
            birthday: computedBirthday
          }
        })

        const data = response && response.data ? response.data : response
        
        if (data && data.patient_id) {
          const newPatientInfo = {
            id: data.patient_id,
            name: form.name,
            mobile: form.mobile,
            gender: form.gender,
            birthday: computedBirthday
          }
          
          patientInfo.value = newPatientInfo
          localStorage.setItem('patient_info', JSON.stringify(newPatientInfo))
          localStorage.setItem('patient_id', data.patient_id) // For easier access if needed
          
          showRegisterModal.value = false
        } else {
          errorMessage.value = '注册失败，未获取到用户ID'
        }
      } catch (error) {
        console.error('Registration error:', error)
        errorMessage.value = error.message || '注册发生错误，请重试'
      } finally {
        loading.value = false
      }
    }

    const goToHistory = () => {
      router.push('/patient/records')
    }

    return {
      currentTab,
      showRegisterModal,
      loading,
      errorMessage,
      form,
      patientInfo,
      handleRegister,
      calculateAge,
      goToHistory
    }
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.animate-fade-in-up {
  animation: fadeInUp 0.5s ease-out forwards;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(20px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
</style>
